import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../../services/apiService';
import api from '../../services/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [loading, setLoading] = useState(false);
  const [verifyingWithGoogle, setVerifyingWithGoogle] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState({
    collegeEmail: '',
    otpCode: '',
    newPassword: '',
    confirmPassword: '',
    isEmailVerified: false
  });
  const [userType, setUserType] = useState('');

  // Check for verified email from Google OAuth on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verifiedEmail = params.get('verified_email');
    const error = params.get('error');
    const errorMessage = params.get('message');

    if (error) {
      toast.error(decodeURIComponent(errorMessage || 'Google verification failed'));
      // Clear URL params
      window.history.replaceState({}, '', '/forgot-password');
    } else if (verifiedEmail) {
      setFormData(prev => ({
        ...prev,
        collegeEmail: decodeURIComponent(verifiedEmail),
        isEmailVerified: true
      }));
      toast.success('Email verified with Google! Enter your new password.');
      // Skip directly to password entry (step 2 for Google-verified users)
      setStep(2);
      // Clear URL params
      window.history.replaceState({}, '', '/forgot-password');
    }
  }, [location]);

  const handleVerifyWithGoogle = async () => {
    setVerifyingWithGoogle(true);
    try {
      const response = await api.get('/auth/google/login-url?state=password-reset');
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate Google verification');
      setVerifyingWithGoogle(false);
    }
  };

  // Step 1: Send OTP (only for email/OTP method)
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.sendForgotPasswordOTP(formData.collegeEmail);
      setUserType(response.userType);
      toast.success('Password reset OTP sent to your email!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Reset Password (with or without OTP)
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        setLoading(false);
        return;
      }

      // Validate password length
      if (formData.newPassword.length < 6) {
        toast.error('Password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      // If verified via Google, use direct reset endpoint
      if (formData.isEmailVerified && !formData.otpCode) {
        await api.post('/auth/forgot-password/reset-google', {
          collegeEmail: formData.collegeEmail,
          newPassword: formData.newPassword
        });
      } else {
        // Traditional OTP-based reset
        await authService.resetPassword(
          formData.collegeEmail,
          formData.otpCode,
          formData.newPassword
        );
      }
      
      toast.success('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await authService.sendForgotPasswordOTP(formData.collegeEmail);
      toast.success('OTP resent successfully!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔐</div>
            <h2 className="text-3xl font-bold text-slate-900">Forgot Password</h2>
            <p className="text-slate-600 mt-2">
              {step === 1 && 'Enter your college email to receive an OTP'}
              {step === 2 && 'Enter the OTP and create a new password'}
            </p>
          </div>

          {/* Step Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                step >= 1 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                1
              </div>
              <div className={`w-16 h-1 ${step >= 2 ? 'bg-primary' : 'bg-slate-200'}`}></div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                step >= 2 ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                2
              </div>
            </div>
          </div>

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              {/* Google OAuth Button - Primary */}
              {!formData.isEmailVerified ? (
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={handleVerifyWithGoogle}
                    disabled={verifyingWithGoogle}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-slate-300 rounded-lg shadow-sm hover:shadow-md hover:border-primary transition-all duration-200"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                    </svg>
                    <span className="text-slate-700 font-semibold text-lg">
                      {verifyingWithGoogle ? 'Redirecting to Google...' : 'Verify Email with Google'}
                    </span>
                  </button>
                  <p className="text-sm text-slate-600 text-center">
                    🔒 Verify your identity with Google to reset password
                  </p>

                  {/* Optional: Email/OTP method toggle */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <button
                        type="button"
                        onClick={() => setShowEmailForm(!showEmailForm)}
                        className="px-4 py-1 bg-white text-slate-500 hover:text-primary transition-colors"
                      >
                        {showEmailForm ? 'Hide' : 'Use'} Email/OTP method
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    College Email *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.collegeEmail}
                      disabled
                      className="w-full px-4 py-3 pr-24 bg-green-50 border-2 border-green-300 rounded-lg text-slate-700 font-medium"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 text-green-600 font-semibold">
                      ✓ Verified
                    </div>
                  </div>
                </div>
              )}

              {/* Email/OTP Form (collapsible) */}
              {showEmailForm && !formData.isEmailVerified && (
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4 animate-fadeIn">
                  <p className="text-sm text-slate-600 mb-3">
                    Alternatively, enter your email to receive an OTP:
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      College Email *
                    </label>
                    <input
                      type="email"
                      required={showEmailForm}
                      placeholder="your.email@cambridge.edu.in"
                      value={formData.collegeEmail}
                      onChange={(e) => setFormData({ ...formData, collegeEmail: e.target.value, isEmailVerified: false })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                    />
                    <p className="text-sm text-slate-500 mt-1">
                      Enter your registered college email address
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !formData.collegeEmail}
                    className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                        Sending OTP...
                      </span>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </div>
              )}

              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:text-primary-dark transition-colors">
                  ← Back to Login
                </Link>
              </div>
            </form>
          )}

          {/* Step 2: Enter OTP and New Password OR Just New Password if Google-verified */}
          {step === 2 && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  {formData.isEmailVerified ? (
                    <>Email verified with Google: <strong>{formData.collegeEmail}</strong></>
                  ) : (
                    <>OTP sent to <strong>{formData.collegeEmail}</strong></>
                  )}
                </p>
                {userType && (
                  <p className="text-xs text-blue-600 mt-1">
                    Account type: {userType === 'student' ? 'Student' : 'Staff'}
                  </p>
                )}
              </div>

              {/* Only show OTP field if NOT verified via Google */}
              {!formData.isEmailVerified && (
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Enter OTP *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="000000"
                    maxLength={6}
                    value={formData.otpCode}
                    onChange={(e) => setFormData({ ...formData, otpCode: e.target.value.replace(/\D/g, '') })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl tracking-widest focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    required
                    placeholder="Enter new password"
                    minLength="6"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    {showNewPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Minimum 6 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    placeholder="Re-enter new password"
                    minLength="6"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || (!formData.isEmailVerified && formData.otpCode.length !== 6)}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-slate-600 hover:text-primary transition-colors"
                >
                  ← Change Email
                </button>
                {!formData.isEmailVerified && (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-primary hover:text-primary-dark transition-colors disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
